import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import '../PrivacyPolicy/PrivacyPolicy.css';

const TermsOfService = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service — Azamra</title>
        <meta name="description" content="Terms of Service governing your access to and use of Azamra Financial Technologies PLC's website, digital platforms, and online services." />
        <meta property="og:title" content="Terms of Service — Azamra" />
        <meta property="og:url" content="https://www.azamra.com/terms-of-service" />
        <link rel="canonical" href="https://www.azamra.com/terms-of-service" />
      </Helmet>
      <Header />

      <section className="legal-hero-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12} className="text-center">
              <h1 className="legal-hero-title">Terms of Service</h1>
              <p className="legal-hero-subtitle">
                The rules and guidelines governing your use of Azamra's platforms and services.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="legal-content-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12}>

              <p className="legal-intro">
                These Terms of Service ("Terms") govern your access to and use of the website, digital
                platforms, and online services operated by <strong>Azamra Financial Technologies PLC</strong>{' '}
                ("Azamra", "we", "our", "us"). By accessing or using our website or services, you agree to be
                bound by these Terms and all applicable laws and regulations in <strong>Cameroon</strong>, the{' '}
                <strong>CEMAC region</strong>, and any other relevant jurisdiction.
              </p>
              <p className="legal-body" style={{ marginTop: '16px' }}>
                If you do not agree to these Terms, please discontinue use of our website and services.
              </p>

              <div className="legal-divider" />

              <h2 className="legal-section-title">1. Use of the Website</h2>
              <p className="legal-body">You may use the Azamra website for lawful purposes only. You agree not to:</p>
              <ul className="legal-list">
                <li>Engage in activities that violate Cameroonian or CEMAC laws</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with website functionality or security</li>
                <li>Use automated tools (bots, scrapers) without permission</li>
                <li>Upload harmful, fraudulent, or misleading content</li>
              </ul>
              <p className="legal-body">
                Azamra reserves the right to restrict or terminate access for any user who violates these Terms.
              </p>

              <h2 className="legal-section-title">2. No Financial or Investment Advice</h2>
              <p className="legal-body">
                Information on our website is provided <strong>for general informational purposes only</strong>. It does <strong>not</strong> constitute:
              </p>
              <ul className="legal-list">
                <li>Financial advice</li>
                <li>Investment recommendations</li>
                <li>Legal or tax guidance</li>
                <li>An offer to buy or sell any tokenized asset</li>
              </ul>
              <p className="legal-body">
                Users should conduct independent due diligence and consult qualified professionals
                before making financial decisions.
              </p>

              <h2 className="legal-section-title">3. Tokenized Assets and Platform Use</h2>
              <p className="legal-body">
                If you engage with Azamra's tokenization platform or related services, you acknowledge that:
              </p>
              <ul className="legal-list">
                <li>Tokenized real-world assets involve <strong>risk</strong>, including market, regulatory, liquidity, and technology risks</li>
                <li>Access to certain services may require identity verification (KYC) and compliance checks</li>
                <li>Azamra may modify, suspend, or discontinue platform features at any time to comply with regulatory requirements</li>
              </ul>
              <p className="legal-body">Use of the platform may be subject to additional agreements or disclosures.</p>

              <h2 className="legal-section-title">4. User Accounts</h2>
              <p className="legal-body">Some services may require creating an account. You agree to:</p>
              <ul className="legal-list">
                <li>Provide accurate and complete information</li>
                <li>Maintain the confidentiality of your login credentials</li>
                <li>Notify us immediately of unauthorized access</li>
              </ul>
              <p className="legal-body">
                Azamra is not responsible for losses arising from compromised accounts due to user negligence.
              </p>

              <h2 className="legal-section-title">5. Intellectual Property</h2>
              <p className="legal-body">
                All content on the Azamra website—including text, graphics, logos, trademarks, software,
                and design—is the property of Azamra or its licensors and is protected under
                Cameroonian, CEMAC, and international intellectual-property laws.
              </p>
              <p className="legal-body">You may not:</p>
              <ul className="legal-list">
                <li>Copy, reproduce, or distribute content without permission</li>
                <li>Use Azamra trademarks without written authorization</li>
                <li>Reverse-engineer or modify any part of the website</li>
              </ul>

              <h2 className="legal-section-title">6. Data Protection and Privacy</h2>
              <p className="legal-body">
                Your use of our website is also governed by our Privacy Policy, which complies with:
              </p>
              <ul className="legal-list">
                <li>Cameroon Law No. 2010/012 on cybersecurity and cybercrime</li>
                <li>CEMAC Regulation 02/18 on personal data protection</li>
              </ul>
              <p className="legal-body">
                By using our services, you consent to the collection and processing of your data as
                described in the Privacy Policy.
              </p>

              <h2 className="legal-section-title">7. Third-Party Links</h2>
              <p className="legal-body">
                Our website may contain links to third-party websites or services. Azamra is <strong>not
                responsible</strong> for:
              </p>
              <ul className="legal-list">
                <li>The content of external sites</li>
                <li>Their privacy practices</li>
                <li>Any damages arising from their use</li>
              </ul>
              <p className="legal-body">Users access third-party sites at their own risk.</p>

              <h2 className="legal-section-title">8. Disclaimers</h2>
              <p className="legal-body">
                Azamra provides its website and services <strong>"as is" and "as available"</strong> without warranties of
                any kind, including:
              </p>
              <ul className="legal-list">
                <li>Accuracy or completeness of information</li>
                <li>Continuous or error-free operation</li>
                <li>Security against all cyber threats</li>
              </ul>
              <p className="legal-body">
                We do not guarantee that the website will always be available or free from disruptions.
              </p>

              <h2 className="legal-section-title">9. Limitation of Liability</h2>
              <p className="legal-body">
                To the fullest extent permitted by law, Azamra is <strong>not liable</strong> for:
              </p>
              <ul className="legal-list">
                <li>Losses arising from use or inability to use our website</li>
                <li>Damages related to tokenized assets or digital transactions</li>
                <li>Errors, interruptions, or security breaches</li>
                <li>Actions of third-party service providers</li>
              </ul>
              <p className="legal-body">
                This limitation applies even if Azamra has been advised of the possibility of such damages.
              </p>

              <h2 className="legal-section-title">10. Compliance and Regulatory Obligations</h2>
              <p className="legal-body">Azamra operates within the regulatory frameworks of:</p>
              <ul className="legal-list">
                <li><strong>MINFI</strong> (Ministry of Finance – Cameroon)</li>
                <li><strong>MINPOSTEL</strong> (Ministry of Post and Telecoms – Cameroon)</li>
                <li><strong>BEAC</strong> (Bank of Central African States)</li>
                <li><strong>COBAC</strong> (Banking Commission of Central Africa)</li>
                <li><strong>CEMAC</strong> regional regulations</li>
              </ul>
              <p className="legal-body">
                We may suspend or modify services to comply with regulatory directives or investigations.
              </p>

              <h2 className="legal-section-title">11. Changes to the Terms</h2>
              <p className="legal-body">
                Azamra may update these Terms at any time. Changes take effect upon posting on this
                page, with the "Last Updated" date revised accordingly.
              </p>
              <p className="legal-body">
                Continued use of the website constitutes acceptance of the updated Terms.
              </p>

              <h2 className="legal-section-title">12. Governing Law</h2>
              <p className="legal-body">These Terms are governed by the laws of:</p>
              <ul className="legal-list">
                <li><strong>The Republic of Cameroon</strong>, and</li>
                <li><strong>CEMAC regional regulations</strong></li>
              </ul>
              <p className="legal-body">Any disputes shall be resolved before competent courts in Cameroon.</p>

              <div className="legal-divider" />

              <div className="legal-contact-block">
                <p className="legal-contact-label">Contact</p>
                <p className="legal-body">
                  <strong>Azamra Financial Technologies PLC</strong> &nbsp;|&nbsp; Email:{' '}
                  <a href="mailto:legal@azamra.com" className="legal-link">legal@azamra.com</a>
                </p>
              </div>

            </Col>
          </Row>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default TermsOfService;
